import type { PuzzleTheme } from '../../../games/analysis/puzzle.ts';
import { Board } from '../match/Board.tsx';
import type { BoardAnnotation } from '../match/Board.tsx';
import { themeVariables } from '../match/presentation.ts';
import type { GameSpec } from '../../../games/spec/schema.ts';
import { usePuzzles } from './usePuzzles.ts';
import type { PuzzleRun } from './usePuzzles.ts';

export interface PuzzlePageProps {
  readonly spec: GameSpec;
  readonly onExit: () => void;
  /** An ISO date to play that day's puzzle. Omitted for a free run. */
  readonly date?: string;
  /** Runs the engine. Defaults to a real worker; tests pass their own. */
  readonly createWorker?: () => Worker;
}

/** Columns are 1-based on screen; the engine counts from zero. */
function columnName(param: number): string {
  return String(param + 1);
}

/**
 * The instruction, and deliberately not the answer.
 *
 * The old brief was "Win in 3", which quietly gave away the motif: a position
 * you have been told is winning is a position you never have to *assess*. Chess
 * dailies hide it for that reason, and working out what the position needs is
 * most of the skill.
 *
 * What is still said is the goal, because that is a fairness line rather than a
 * hint: hunting for a win that does not exist is not a puzzle, it is a trick.
 * "There is a win here" covers both winning motifs at once, so it never reveals
 * whether the win starts by defending.
 */
function brief(run: PuzzleRun): string {
  if (run.puzzle === null) return 'Looking for a position…';
  if (run.puzzle.goal === 'hold') return 'You are in trouble. Hold the draw.';
  return run.puzzle.winIn === 2 ? 'There is a win here, in 2 moves' : `There is a win here, in ${String(run.puzzle.winIn)} moves`;
}

/** The motif, named only once it has been found. */
const THEME_NAMES: Readonly<Record<PuzzleTheme, string>> = Object.freeze({
  mate: 'A forced win',
  defend_and_win: 'Block, then counter-attack',
  survive: 'The only move that holds',
});

/** What the motif was, explained after the fact. This is the teaching moment. */
const THEME_NOTES: Readonly<Record<PuzzleTheme, string>> = Object.freeze({
  mate: 'Nothing of theirs needed answering — the whole board was yours to finish.',
  defend_and_win:
    'Their threat had to be answered, and the square that answered it was the same square that won. Look for the move that does both before you look for either.',
  survive: 'No win was on offer. Every other move lost, so finding the one that held was the whole game.',
});

/**
 * What the screen says about where you are.
 *
 * Every branch is a fact about the position rather than praise or scolding: the
 * player can see they got it wrong, and what they need is the number they were
 * playing for.
 */
function statusLine(run: PuzzleRun): string {
  switch (run.phase) {
    case 'seeking':
      return 'Finding a position with a forced win\u2026';
    case 'checking':
      return 'Checking\u2026';
    case 'solved':
      return run.puzzle === null
        ? 'Solved.'
        : `Solved. ${THEME_NOTES[run.puzzle.theme]}`;
    case 'wrong':
      return run.missed === null
        ? 'That lets it slip. The position is still there \u2014 try again.'
        : `That lets it slip: the win in ${String(run.missed)} is gone. Try again.`;
    case 'stuck':
      return 'No puzzle could be proven right now.';
    default:
      if (run.puzzle?.goal === 'hold') {
        return 'One move holds this. Everything else loses.';
      }
      return run.movesLeft === 1
        ? 'One move finishes it.'
        : `${String(run.movesLeft)} moves left. They will fight back.`;
  }
}

/**
 * Puzzles: find the whole win, not just the next move.
 *
 * The screen is one board and one sentence. Everything that could be a badge,
 * a timer or a streak counter has been left out on purpose — a puzzle run is a
 * thing you do between games, and the moment it starts keeping score it becomes
 * another rating to protect.
 *
 * Two things carry the teaching:
 *
 *  - **The move count is the brief.** "Win in 3" tells the player a line exists
 *    and that one good move will not finish it, which is the entire difference
 *    between this and the coach.
 *  - **A wrong move is not an ending.** The board goes back and says what the
 *    move cost. Being wrong in a place where nothing is at stake is the cheapest
 *    lesson available.
 */
export function PuzzlePage({ spec, onExit, createWorker, date }: PuzzlePageProps) {
  const { run, drop, retry, show, next } = usePuzzles({
    spec,
    ...(createWorker ? { createWorker } : {}),
    ...(date ? { date } : {}),
  });
  const daily = date !== undefined;

  const waiting = run.phase === 'seeking' || run.phase === 'checking';
  // Still playable while a hint is being worked out: a player who changes their
  // mind and just plays the move should not be blocked by their own question.
  const playable = run.phase === 'solving';

  /*
   * The answer, drawn on the board rather than described.
   *
   * A column number has to be translated back into a place before it is any
   * use; a ghost disc is already there. Shown only when asked for.
   */
  const answer: BoardAnnotation | null =
    run.shown === null || run.yourSeat === null
      ? null
      : {
          column: run.shown,
          seat: run.yourSeat,
          label: `the winning move is column ${columnName(run.shown)}`,
        };

  return (
    <section className="puzzle" aria-labelledby="puzzle-heading" style={themeVariables(spec)}>
      <header className="puzzle__head">
        <h2 id="puzzle-heading" className="puzzle__heading">
          {daily ? 'Puzzle of the day' : 'Puzzles'}
        </h2>
        <p className="puzzle__intro">
          {daily
            ? 'One position, the same one for everybody today. Work out what it needs — sometimes a win, sometimes only a save.'
            : 'A position that needs working out. Sometimes a forced win, sometimes the one move that saves you.'}
        </p>
      </header>

      <div className="puzzle__layout">
        <div className="puzzle__stage">
          <p className="puzzle__brief">{brief(run)}</p>
          <Board
            spec={spec}
            board={run.board}
            yourSeat={run.yourSeat}
            legalColumns={run.legalColumns}
            pendingColumn={null}
            lastMoveIndex={run.lastMoveCell}
            winningLine={run.winningLine}
            interactive={playable}
            onDrop={drop}
            annotation={answer}
            /* No column buttons before there is a puzzle to play into. */
            columnControls={run.puzzle !== null}
          />
        </div>

        <div className="puzzle__side">
          {/*
            One live region for the whole run. The board is already announced
            move by move; a second channel narrating the same events would talk
            over the sentence that says what to do next.
          */}
          <p className="puzzle__status" role="status">
            {waiting ? <span className="spinner" aria-hidden="true" /> : null}
            {statusLine(run)}
          </p>

          {/* The motif, named only now. Naming it earlier would have been
              naming most of the answer; naming it here is the thing the player
              takes to their next game. */}
          {run.phase === 'solved' && run.puzzle !== null ? (
            <p className="puzzle__theme">{THEME_NAMES[run.puzzle.theme]}</p>
          ) : null}

          {run.error !== null ? <p className="puzzle__error">{run.error}</p> : null}

          <div className="puzzle__actions">
            {run.phase === 'wrong' ? (
              <button type="button" className="button button--primary" onClick={retry}>
                Try again
              </button>
            ) : null}

            {/* No "next" on the daily. A daily you can advance past is just a
                puzzle list with a date on it, and there is nothing left to
                compare with anybody. */}
            {run.phase === 'solved' && !daily ? (
              <button type="button" className="button button--primary" onClick={next}>
                {run.nextReady ? 'Next puzzle' : 'Next puzzle (finding\u2026)'}
              </button>
            ) : null}

            {run.phase === 'solved' && daily ? (
              <p className="puzzle__done">That is today&rsquo;s. Back tomorrow.</p>
            ) : null}

            {/*
              Being told is always available and always costs the same thing:
              the puzzle stops counting as solved. Hiding the answer behind a
              streak would just teach people to guess.
            */}
            {(run.phase === 'solving' || run.phase === 'wrong') && run.shown === null ? (
              <button
                type="button"
                className="button button--quiet"
                onClick={show}
                disabled={run.asking}
              >
                {run.asking ? 'Working it out…' : 'Show me the move'}
              </button>
            ) : null}

            {/* No skip on the daily: a puzzle you can reroll is not a daily,
                and there would be nothing left to compare with anyone. */}
            {run.phase !== 'solved' && run.puzzle !== null && !daily ? (
              <button type="button" className="button button--quiet" onClick={next}>
                Skip
              </button>
            ) : null}
          </div>

          {daily ? null : (
            <p className="puzzle__tally">
              {run.attempted === 0
                ? 'Nothing solved yet.'
                : `Solved ${String(run.solvedCount)} of ${String(run.attempted)} without help.`}
            </p>
          )}

          <footer className="puzzle__exit">
            <button type="button" className="button button--quiet" onClick={onExit}>
              Done
            </button>
          </footer>
        </div>
      </div>
    </section>
  );
}
